import { InPageMessage } from '@liveauctioneers/caterwaul-components/lib/InPageMessage/InPageMessage';
import { InlineButton } from '@liveauctioneers/hammer-ui-core/inlineButton';
import { FormattedMessage, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import React from 'react';
import styled, { css } from 'styled-components';

import { PaymentProviders } from '@/enums/paymentProviders';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { fetchBidderCreditCardsIfNeeded } from '@/redux/modules/account/payments/creditCard/creditCard/creditCard.actions';
import { getDefaultBidderCreditCard } from '@/redux/modules/account/payments/creditCard/creditCard/creditCard.selectors';
import { fetchBidderDataIfNeeded } from '@/redux/modules/account/user/user.actions';
import {
    getBidderMobileVerified,
    getDefaultAddress,
    getDefaultAddressIsVerified,
    isUserAtLeastPartiallyRegistered,
} from '@/redux/modules/account/user/user.selectors';
import { openCompleteAccountSignUpModal } from '@/redux/modules/modal';
import { getIsWhiteLabel } from '@/redux/modules/whiteLabel';
import { useTrackCompleteAccountSetupBannerClicked } from '@/utils/amplitude/analytics';

interface CompleteAccountBannerProps {
    className?: string;
}

export const CompleteAccountBanner = ({ className }: CompleteAccountBannerProps) => {
    const dispatch = useAppDispatch();
    const { formatMessage } = useIntl();
    const isWhiteLabel = useAppSelector(getIsWhiteLabel);
    const userPartiallyRegistered = useAppSelector(isUserAtLeastPartiallyRegistered);
    const defaultAddress = useAppSelector(getDefaultAddress);
    const addressVerified = useAppSelector(getDefaultAddressIsVerified);
    const defaultCreditCard = useAppSelector((state) => getDefaultBidderCreditCard(state, PaymentProviders.Payrix));
    const mobileVerified = useAppSelector(getBidderMobileVerified);
    const trackCompleteAccountSetupBannerClicked = useTrackCompleteAccountSetupBannerClicked();

    const showCompleteAccount =
        userPartiallyRegistered &&
        !(
            Boolean(defaultAddress?.firstName) &&
            Boolean(defaultAddress?.lastName) &&
            Boolean(defaultAddress?.address1) &&
            addressVerified &&
            Boolean(defaultCreditCard?.id) &&
            mobileVerified
        );

    const handleSetupLink = () => {
        dispatch(openCompleteAccountSignUpModal());
        trackCompleteAccountSetupBannerClicked();
    };

    React.useEffect(() => {
        dispatch(fetchBidderDataIfNeeded({ force: true }));
        dispatch(fetchBidderCreditCardsIfNeeded());
    }, [dispatch]);

    return (
        showCompleteAccount && (
            <StyledInPageMessage
                $showBottomMargin={isWhiteLabel}
                className={className}
            >
                <InlineButton
                    onClick={handleSetupLink}
                    size="sm"
                >
                    {formatMessage({ id: 'completeAccount.setupLink' })}
                </InlineButton>
                <FormattedMessage id="completeAccount.toSaveTimeBidding" />
            </StyledInPageMessage>
        )
    );
};

export default CompleteAccountBanner;

const StyledInPageMessage = styled(InPageMessage)<{
    $showBottomMargin: boolean;
}>`
    width: 100%;
    margin: 32px auto 0;
    ${({ $showBottomMargin }) => {
        if ($showBottomMargin) {
            return css`
                margin: 32px auto;
            `;
        } else {
            return css`
                margin: 32px auto 0;
            `;
        }
    }}

    max-width: ${({ theme }) => theme.breakpoints.pageWidthNum}px;

    @media (max-width: ${({ theme }) => theme.breakpoints.pageWidth}) {
        width: auto;
        margin-left: 0;
        margin-right: 0;
    }
`;
